<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const steps = [
  {
    number: '1',
    title: 'Buat Sesi Opname',
    color: 'primary',
    icon: 'ri-add-circle-line',
    desc: 'Tentukan tanggal, pilih bahan baku yang akan dihitung. Sistem otomatis mencatat stok saat ini sebagai angka pembanding.',
  },
  {
    number: '2',
    title: 'Hitung Fisik di Gudang',
    color: 'warning',
    icon: 'ri-hand-coin-line',
    desc: 'Matikan layar dan pergi ke gudang. Hitung secara fisik jumlah setiap bahan baku yang ada. Catat di kertas atau aplikasi notes.',
  },
  {
    number: '3',
    title: 'Input Stok Aktual',
    color: 'info',
    icon: 'ri-keyboard-line',
    desc: 'Kembali ke sistem. Input hasil hitungan fisik di kolom "Stok Aktual". Sistem otomatis hitung selisih dengan stok sistem.',
  },
  {
    number: '4',
    title: 'Review Selisih',
    color: 'secondary',
    icon: 'ri-file-search-line',
    desc: 'Cek bahan mana yang ada selisih. Selisih negatif artinya stok nyata lebih sedikit dari data sistem — perlu investigasi.',
  },
  {
    number: '5',
    title: 'Konfirmasi Opname',
    color: 'success',
    icon: 'ri-check-double-line',
    desc: 'Setelah yakin semua angka benar, klik Konfirmasi. Stok semua bahan otomatis disesuaikan dengan hasil hitungan fisik.',
  },
]

const faq = [
  {
    q: 'Seberapa sering harus melakukan Stock Opname?',
    a: 'Untuk UMKM, disarankan minimal 1 bulan sekali. Toko dengan perputaran stok tinggi (coffee shop sibuk) sebaiknya 2 minggu sekali. Lakukan di saat operasional sedang tutup atau sepi — biasanya dini hari atau setelah jam tutup toko.',
  },
  {
    q: 'Apa yang terjadi jika saya klik Konfirmasi padahal ada angka yang salah?',
    a: 'Stok akan langsung disesuaikan berdasarkan angka yang Anda input. Perubahan tidak bisa dibatalkan. Jika ada angka yang salah, Anda perlu melakukan Stock Adjustment manual untuk mengkoreksi bahan yang bersangkutan setelah opname selesai.',
  },
  {
    q: 'Apakah stok bisa berubah selama saya sedang melakukan opname?',
    a: 'Ya, bisa berubah jika ada transaksi penjualan atau pembelian yang masuk saat sesi opname masih berstatus Draft. Untuk akurasi terbaik, lakukan opname saat toko sedang tutup dan tidak ada aktivitas kasir. Atau pastikan tidak ada transaksi yang diproses selama penghitungan berlangsung.',
  },
  {
    q: 'Apakah saya harus menghitung SEMUA bahan baku setiap opname?',
    a: 'Tidak harus. Saat buat sesi opname, Anda bisa memilih hanya bahan-bahan tertentu yang ingin dihitung. Contoh: bulan ini fokus hitung bahan mahal dulu (kopi, susu), bulan depan hitung bahan pendukung (gula, es batu, cup). Ini disebut "cycle count" dan lebih praktis untuk toko besar.',
  },
  {
    q: 'Saya klik Batalkan, apakah data stok berubah?',
    a: 'Tidak. Jika Anda klik Batalkan Opname, stok tidak berubah sama sekali. Sesi opname berubah status menjadi "Dibatalkan" dan tidak bisa dibuka kembali. Buat sesi baru jika ingin mencoba lagi.',
  },
  {
    q: 'Apa arti warna pada kolom "Selisih"?',
    a: 'Hijau = tidak ada selisih (stok sistem sama dengan stok fisik). Biru = stok fisik lebih banyak dari sistem (stok berlebih — mungkin ada pembelian yang belum dicatat). Merah = stok fisik lebih sedikit dari sistem (stok kurang — mungkin ada kebocoran, tumpah, atau theft).',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink
            to="/userguide/produk-inventory"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Produk &amp; Inventory
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Stock Opname</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="success" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-clipboard-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Stock Opname</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Audit stok menyeluruh — bandingkan data sistem dengan hitungan fisik di gudang
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda melakukan stock opname dari awal hingga konfirmasi —
          proses penghitungan fisik stok yang memastikan data di sistem sesuai dengan kondisi nyata di gudang.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="success" />
            Apa itu Stock Opname?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Bayangkan Anda punya dompet. Di aplikasi rekening bank tertulis saldo Rp 500.000,
              tapi saat Anda hitung uang fisik di dompet ternyata hanya Rp 480.000.
              Ada selisih Rp 20.000 yang perlu dicari tahu penyebabnya.
            </p>

            <p class="text-body-2 mb-3">
              <strong>Stock Opname</strong> bekerja dengan cara yang sama, tapi untuk bahan baku.
              Sistem PintuKasir mencatat stok secara otomatis setiap ada transaksi.
              Tapi di dunia nyata, selalu ada kemungkinan terjadi selisih karena:
              bahan tumpah, pencatatan yang terlewat, atau penguapan alami (untuk bahan cair).
            </p>

            <VAlert type="success" variant="tonal" class="mb-3">
              Dengan rutin melakukan Stock Opname, Anda <strong>memastikan HPP (Harga Pokok Penjualan)
              yang dihitung sistem selalu akurat</strong> — sehingga laporan laba rugi Anda bisa dipercaya.
            </VAlert>

            <VAlert type="warning" variant="tonal" density="compact">
              <strong>Rekomendasi waktu terbaik:</strong>
              Lakukan Stock Opname saat toko <strong>sedang tutup</strong> —
              dini hari, setelah jam tutup, atau hari libur.
              Hindari melakukan opname saat kasir sedang memproses transaksi
              karena stok sistem bisa berubah selama penghitungan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Alur Proses -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-route-line" class="me-2" color="primary" />
            Alur Proses Stock Opname (5 Langkah)
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol
                v-for="step in steps"
                :key="step.number"
                cols="12"
                md="4"
                lg="12"
              >
                <div class="d-flex gap-4 mb-4">
                  <VAvatar :color="step.color" variant="tonal" size="44" rounded class="flex-shrink-0 mt-1">
                    <VIcon :icon="step.icon" size="20" />
                  </VAvatar>
                  <div>
                    <div class="d-flex align-center gap-2 mb-1">
                      <VChip :color="step.color" size="x-small" label>
                        Langkah {{ step.number }}
                      </VChip>
                      <span class="text-body-2 font-weight-bold">{{ step.title }}</span>
                    </div>
                    <p class="text-body-2 text-medium-emphasis mb-0">{{ step.desc }}</p>
                  </div>
                </div>
                <VDivider v-if="step.number < '5'" class="mb-4" />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- LANGKAH 1: Buat Sesi -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="primary" />
            Detail Langkah 1 — Membuat Sesi Opname Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-3">
                Buka menu <strong>Inventory → Stock Opname</strong> di sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Buat Opname Baru"</strong> di pojok kanan atas
              </li>
              <li class="mb-3">
                Isi form yang muncul:
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 160px">Field</th>
                      <th style="min-width: 80px">Wajib?</th>
                      <th>Penjelasan &amp; Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Tanggal Opname</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Tanggal penghitungan fisik dilakukan.
                        <br>Contoh: <em>5 Juni 2025</em>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Catatan</strong></td>
                      <td><VChip color="success" size="x-small" variant="tonal">❌ Opsional</VChip></td>
                      <td class="text-body-2 py-2">
                        Keterangan tambahan untuk sesi ini.
                        <br>Contoh: <em>"Opname rutin awal bulan Juni 2025"</em>
                        atau <em>"Opname pasca event catering 3 Juni"</em>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Pilih Bahan Baku</strong></td>
                      <td><VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip></td>
                      <td class="text-body-2 py-2">
                        Centang bahan baku yang akan dihitung.
                        Gunakan <strong>"Pilih Semua"</strong> untuk audit menyeluruh,
                        atau centang manual untuk audit sebagian bahan saja.
                        <br>
                        <span class="text-caption text-medium-emphasis">
                          Di sebelah kanan nama bahan, Anda bisa lihat stok sistem saat ini sebagai referensi.
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-0">
                Klik <strong>"Buat Sesi Opname"</strong>.
                Sistem membuat sesi baru dengan status <strong>Draft</strong>
                dan mencatat snapshot stok sistem saat ini.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- LANGKAH 3: Input Stok Aktual -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-keyboard-line" class="me-2" color="info" />
            Detail Langkah 3 — Input Stok Aktual (Hasil Hitung Fisik)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setelah kembali dari gudang dengan catatan hasil hitung fisik,
              buka kembali sesi opname. Ikuti langkah berikut:
            </p>

            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-3">
                Di halaman detail sesi opname, Anda akan melihat tabel daftar bahan baku.
                Kolom <strong>"Stok Sistem"</strong> sudah terisi otomatis dari data saat sesi dibuat.
              </li>
              <li class="mb-3">
                Klik ikon <strong>pensil (✏️)</strong> di baris bahan baku yang ingin diisi.
                Baris tersebut akan berubah menjadi mode edit.
              </li>
              <li class="mb-3">
                Isi kolom <strong>"Stok Aktual"</strong> dengan hasil hitungan fisik Anda.
                <VAlert type="warning" variant="tonal" density="compact" class="mt-2 mb-2">
                  <strong>Penting:</strong> Isi jumlah TOTAL stok yang ada di gudang,
                  bukan selisihnya. Contoh: jika Anda hitung fisik ada 2.350 gram kopi →
                  isi <strong>2.350</strong>.
                </VAlert>
                Sistem otomatis menghitung selisih: Stok Aktual − Stok Sistem = Selisih.
              </li>
              <li class="mb-3">
                Isi <strong>Catatan</strong> jika ada keterangan khusus untuk bahan ini
                (opsional). Contoh: <em>"Termasuk stok di kulkas belakang"</em>
              </li>
              <li class="mb-3">
                Klik ikon <strong>centang (✓)</strong> untuk menyimpan baris tersebut.
                Atau klik ikon <strong>silang (✗)</strong> untuk membatalkan edit.
              </li>
              <li class="mb-0">
                Ulangi untuk semua bahan baku yang ada di daftar.
              </li>
            </ol>

            <p class="text-body-2 font-weight-bold mb-2">📋 Penjelasan Kolom Tabel:</p>
            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 140px">Kolom</th>
                  <th>Arti &amp; Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Stok Sistem</strong></td>
                  <td class="text-body-2 py-2">
                    Angka stok di sistem saat sesi opname dibuat. Ini adalah angka pembanding.
                    <br>Contoh: <em>2.480 gram</em>
                  </td>
                </tr>
                <tr>
                  <td><strong>Stok Aktual</strong></td>
                  <td class="text-body-2 py-2">
                    Angka hasil hitungan fisik Anda. Anda isi secara manual.
                    <br>Contoh: <em>2.350 gram</em>
                  </td>
                </tr>
                <tr>
                  <td><strong>Selisih</strong></td>
                  <td class="text-body-2 py-2">
                    Otomatis dihitung: Stok Aktual − Stok Sistem.
                    <br>
                    <VChip color="success" size="x-small" variant="tonal">Hijau (0)</VChip>
                    = tidak ada selisih<br>
                    <VChip color="info" size="x-small" variant="tonal">Biru (+)</VChip>
                    = stok fisik lebih banyak<br>
                    <VChip color="error" size="x-small" variant="tonal">Merah (−)</VChip>
                    = stok fisik lebih sedikit<br>
                    <br>Contoh: 2.350 − 2.480 = <span class="text-error font-weight-bold">−130 gram</span>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- LANGKAH 5: Konfirmasi -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-check-double-line" class="me-2" color="success" />
            Detail Langkah 5 — Konfirmasi Opname
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Setelah semua stok aktual sudah diinput dan Anda sudah review selisihnya:
            </p>

            <ol class="text-body-2 ps-4 mb-4">
              <li class="mb-3">
                Cek ringkasan di bagian atas halaman:
                <strong>Total Item</strong> (berapa bahan yang dihitung) dan
                <strong>Ada Selisih</strong> (berapa bahan yang stoknya berbeda).
              </li>
              <li class="mb-3">
                Jika ada selisih yang mencurigakan, investigasi dulu sebelum konfirmasi.
                Selisih besar bisa jadi tanda kehilangan stok yang perlu ditangani.
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Konfirmasi"</strong> berwarna hijau di bagian atas.
              </li>
              <li class="mb-3">
                Dialog konfirmasi akan muncul. Baca peringatan yang ditampilkan,
                lalu klik <strong>"Ya, Konfirmasi"</strong>.
              </li>
              <li class="mb-0">
                Sistem otomatis menyesuaikan stok semua bahan yang ada selisih.
                Status sesi berubah dari <strong>Draft</strong> menjadi <strong>Dikonfirmasi</strong>
                dan tercatat di Riwayat Pergerakan Stok dengan tipe "Stock Opname".
              </li>
            </ol>

            <VAlert type="error" variant="tonal">
              <strong>⚠️ Setelah dikonfirmasi, tidak bisa diubah.</strong>
              Penyesuaian stok bersifat final. Pastikan semua angka sudah
              benar-benar Anda periksa sebelum klik konfirmasi.
              Jika ternyata ada yang salah, lakukan Stock Adjustment manual
              untuk bahan yang bersangkutan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Status Opname -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-information-line" class="me-2" color="primary" />
            Status Sesi Opname
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol cols="12" md="4">
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-3 text-center">
                    <VChip color="warning" label class="mb-2">Draft</VChip>
                    <p class="text-body-2 mb-0">
                      Sesi sedang berjalan. Anda masih bisa input dan edit stok aktual.
                      Stok di sistem <strong>belum berubah</strong>.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-3 text-center">
                    <VChip color="success" label class="mb-2">Dikonfirmasi</VChip>
                    <p class="text-body-2 mb-0">
                      Opname selesai. Stok sudah disesuaikan dengan hasil hitungan fisik.
                      Data <strong>tidak bisa diubah</strong>.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-3 text-center">
                    <VChip color="error" label class="mb-2">Dibatalkan</VChip>
                    <p class="text-body-2 mb-0">
                      Opname dibatalkan sebelum konfirmasi. Stok
                      <strong>tidak berubah sama sekali</strong>.
                      Buat sesi baru jika ingin coba lagi.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="success" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis untuk Stock Opname yang Akurat
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Siapkan form hitungan fisik terlebih dahulu.</strong>
                  Sebelum buat sesi di sistem, cetak atau tulis daftar bahan yang akan dihitung.
                  Ini membantu karyawan yang bertugas hitung tidak ada yang terlewat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Hitung dua kali untuk bahan mahal.</strong>
                  Untuk bahan berharga tinggi seperti kopi specialty atau daging,
                  minta dua orang menghitung secara terpisah.
                  Jika hasilnya sama, baru masukkan ke sistem.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Investigasi selisih besar sebelum konfirmasi.</strong>
                  Selisih lebih dari 10% dari stok sistem sebaiknya dicari dulu penyebabnya.
                  Mungkin ada bahan yang belum dihitung, atau ada pencatatan pembelian yang terlewat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan opname parsial untuk efisiensi.</strong>
                  Anda tidak harus hitung semua bahan setiap bulan.
                  Bagi bahan menjadi 2-3 kelompok, dan rotasi setiap 2 minggu.
                  Ini disebut <em>cycle count</em> dan lebih mudah dilakukan rutin.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Pergerakan Stok', sub: 'Lihat hasil penyesuaian setelah opname', icon: 'ri-history-line', color: 'secondary', to: '/userguide/produk-inventory/pergerakan-stok' },
              { title: 'Stock Adjustment', sub: 'Koreksi cepat untuk satu bahan', icon: 'ri-equalizer-line', color: 'warning', to: '/userguide/produk-inventory/stock-adjustment' },
              { title: 'Laporan Inventory', sub: 'Lihat valuasi stok setelah opname', icon: 'ri-bar-chart-line', color: 'info', to: '/userguide/laporan/inventory' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
